import { pageTitles } from '@components/PageTitle/pageTitles';
import { logout } from '@features/auth/auth.slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

declare global {
    interface WindowEventMap {
        nativeBack: Event;
        appExit: Event;
    }
    interface Window {
        AndroidInterface: {
            exitApp: () => void;
            getFcmToken: () => string | null;
        };
    }
}

export const useNativeBack = () => {
    const [displayExitConfirmationPopup, setDisplayExitConfirmationPopup] = useState<boolean>(false);

    const navigate = useNavigate();
    const location = useLocation();
    const locationRef = useRef(location);
    const dispatch = useDispatch();

    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    const handleNativeBack = useCallback((): void => {
        const routeInformations = pageTitles[locationRef.current.pathname];

        if (routeInformations?.customNavigate) {
            navigate(routeInformations.customNavigate);
            return;
        }

        if (locationRef.current.pathname === '/home' && !displayExitConfirmationPopup) {
            setDisplayExitConfirmationPopup(true);
            return;
        }

        setDisplayExitConfirmationPopup(false);
        navigate('/home');
    }, [navigate, displayExitConfirmationPopup]);

    const handleAppExit = () => {
        setDisplayExitConfirmationPopup(false);
        dispatch(logout());
        if (window?.AndroidInterface?.exitApp) {
            window.AndroidInterface.exitApp();
        }
    };

    useEffect(() => {
        window.addEventListener('nativeBack', handleNativeBack);
        return () => {
            window.removeEventListener('nativeBack', handleNativeBack);
        };
    }, [handleNativeBack]);

    return {
        displayExitConfirmationPopup,
        setDisplayExitConfirmationPopup,
        handleAppExit
    };
};
